<template>
  <div>
    <div v-if="!loading">
      <!-- Year / Month selector -->
      <div class="flex flex-wrap items-center gap-2 mb-2">
        <button
          v-for="(year, index) in availableYears"
          :key="index"
          :class="[
            'px-3 py-1 rounded-full text-sm font-medium transition-colors',
            selectedYear === year
              ? 'bg-primary-500 text-white shadow'
              : 'bg-white text-content-300 shadow hover:text-primary-500',
          ]"
          @click="selectYear(year)"
        >
          {{ year }}
        </button>
      </div>

      <div class="flex flex-wrap items-center gap-2 mb-6">
        <button
          v-for="(month, index) in availableMonths"
          :key="index"
          :class="[
            'px-3 py-1 rounded-full text-sm font-medium transition-colors',
            selectedMonth === month.number
              ? 'bg-primary-500 text-white shadow'
              : 'bg-white text-content-300 shadow hover:text-primary-500',
          ]"
          @click="selectMonth(month)"
        >
          {{ month.label }}
        </button>
      </div>

      <!-- Empty state when no month is selected -->
      <div v-if="!report && selectedMonth === null" class="card text-center py-10">
        <p class="text-content-300">Selecciona un mes para ver el reporte.</p>
      </div>

      <div v-if="report">
        <!-- General stats grid -->
        <h2 class="text-lg font-semibold text-gray-700 mb-3">General</h2>
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Planes (bruto)</p>
            <p class="text-2xl font-bold text-gray-800">{{ integerToCurrency(report.gross_amount) }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Desc. primera compra</p>
            <p class="text-2xl font-bold text-error-500">− {{ integerToCurrency(report.coupon_discount_amount) }}</p>
            <p class="text-xs text-gray-400 mt-1">Lo absorbe la sucursal que captó al cliente</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Monedero canjeado</p>
            <p class="text-2xl font-bold text-error-500">− {{ integerToCurrency(report.wallet_applied_amount) }}</p>
            <p class="text-xs text-gray-400 mt-1">Lo absorbe la sucursal donde se canjeó</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Planes (neto cobrado)</p>
            <p class="text-2xl font-bold text-gray-800">{{ integerToCurrency(report.stripe_amount) }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Reembolsado (Stripe)</p>
            <p class="text-2xl font-bold text-error-500">{{ integerToCurrency(report.stripe_refunded_amount) }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Ventas Tarjeta / Efectivo</p>
            <p class="text-2xl font-bold text-gray-800">{{ integerToCurrency(report.incomes_amount) }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4 border-l-4 border-primary-500">
            <p class="text-sm text-gray-500 mb-1">Total</p>
            <p class="text-2xl font-bold text-primary-500">{{ integerToCurrency(report.total) }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Número de tickets</p>
            <p class="text-2xl font-bold text-gray-800">{{ report.number_of_tickets }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Ticket promedio</p>
            <p class="text-2xl font-bold text-gray-800">{{ integerToCurrency(report.average_per_ticket) }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Clientes nuevos</p>
            <p class="text-2xl font-bold text-success-500">{{ report.new_clients }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Tasa de conversión</p>
            <p class="text-2xl font-bold text-success-500">{{ parseFloat(report.percentage_with_active_quotes).toFixed(1) }}%</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Cancelaciones</p>
            <p class="text-2xl font-bold text-error-500">{{ report.canceled_quotes }}</p>
          </div>
        </div>

        <!-- Ingresos / Egresos row -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <!-- Ingresos -->
          <div class="bg-white rounded-lg shadow p-4">
            <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Ingresos</h3>
            <div class="flex items-center justify-between py-2 border-b border-gray-100">
              <span class="text-sm text-gray-600">Cuotas de franquicias</span>
              <span class="font-semibold text-success-500">{{ integerToCurrency(report.franchise_income_amount) }}</span>
            </div>
          </div>

          <!-- Egresos -->
          <div class="bg-white rounded-lg shadow p-4">
            <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Egresos</h3>
            <div
              v-for="expense in report.expenses_by_category"
              :key="expense.expense_category_id"
              class="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
            >
              <span class="text-sm text-gray-600">{{ expense.category_name }}</span>
              <span class="font-semibold text-error-500">{{ integerToCurrency(expense.total) }}</span>
            </div>
            <p v-if="!report.expenses_by_category || report.expenses_by_category.length === 0" class="text-sm text-content-300 py-2">Sin egresos registrados</p>
          </div>
        </div>

        <!-- Franchises section -->
        <h2 class="text-lg font-semibold text-gray-700 mb-3">Franquicias</h2>

        <div
          v-if="!report.franchises || report.franchises.length === 0"
          class="card text-center py-6"
        >
          <p class="text-content-300">Sin datos para este período</p>
        </div>

        <div class="flex flex-col gap-4">
          <div
            v-for="franchise in report.franchises"
            :key="franchise.id"
            class="bg-white rounded-lg shadow overflow-hidden"
          >
            <!-- Franchise header — clickable to expand -->
            <div
              class="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
              @click="toggleFranchise(franchise.id)"
            >
              <div class="flex items-center gap-3">
                <div class="w-2 h-8 rounded-full bg-primary-500"></div>
                <div>
                  <h3 class="font-semibold text-gray-800">{{ franchise.name }}</h3>
                  <p class="text-xs text-gray-500">{{ franchise.branches ? franchise.branches.length : 0 }} sucursal(es)</p>
                </div>
              </div>
              <div class="flex items-center gap-6">
                <div class="text-right hidden sm:block">
                  <p class="text-xs text-gray-500">Publicidad</p>
                  <p class="font-semibold text-gray-700">{{ integerToCurrency(franchise.advertisement_fee) }}</p>
                </div>
                <div class="text-right hidden sm:block">
                  <p class="text-xs text-gray-500">Mantenimiento</p>
                  <p class="font-semibold text-gray-700">{{ integerToCurrency(franchise.maintenance_fee) }}</p>
                </div>
                <!-- Chevron indicator -->
                <svg
                  :class="['w-5 h-5 text-gray-400 transition-transform', isFranchiseExpanded(franchise.id) ? 'rotate-180' : '']"
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            <!-- Expanded: fees summary on small screens + branch table -->
            <div v-if="isFranchiseExpanded(franchise.id)" class="border-t border-gray-100">
              <!-- Fees row for mobile -->
              <div class="flex gap-4 px-5 py-3 bg-gray-50 sm:hidden">
                <div>
                  <p class="text-xs text-gray-500">Publicidad</p>
                  <p class="font-semibold text-gray-700">{{ integerToCurrency(franchise.advertisement_fee) }}</p>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Mantenimiento</p>
                  <p class="font-semibold text-gray-700">{{ integerToCurrency(franchise.maintenance_fee) }}</p>
                </div>
              </div>

              <!-- Branch table -->
              <div class="overflow-x-auto">
                <table class="min-w-full text-sm">
                  <thead class="bg-primary-100">
                    <tr>
                      <th class="px-5 py-2 text-left text-xs font-semibold text-primary-700 uppercase tracking-wide">Sucursal</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Planes bruto</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Cupón 1ª compra</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Monedero</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Planes neto</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Reembolsos</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Tarjeta/Efectivo</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Total</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Tickets</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Ticket prom.</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Nuevos</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Prom. nuevos</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Cancelaciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(branch, bIndex) in franchise.branches"
                      :key="branch.branch_id"
                      :class="bIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                    >
                      <td class="px-5 py-2 font-medium text-gray-800">{{ branch.branch_name }}</td>
                      <td class="px-4 py-2 text-right text-gray-700">{{ integerToCurrency(branch.gross_amount) }}</td>
                      <td class="px-4 py-2 text-right text-error-500">{{ branch.coupon_discount_amount > 0 ? '− ' + integerToCurrency(branch.coupon_discount_amount) : '—' }}</td>
                      <td class="px-4 py-2 text-right text-error-500">{{ branch.wallet_applied_amount > 0 ? '− ' + integerToCurrency(branch.wallet_applied_amount) : '—' }}</td>
                      <td class="px-4 py-2 text-right text-gray-700">{{ integerToCurrency(branch.stripe_amount) }}</td>
                      <td class="px-4 py-2 text-right text-error-500">{{ integerToCurrency(branch.stripe_refunded_amount) }}</td>
                      <td class="px-4 py-2 text-right text-gray-700">{{ integerToCurrency(branch.incomes_amount) }}</td>
                      <td class="px-4 py-2 text-right font-semibold text-primary-500">{{ integerToCurrency(branch.total) }}</td>
                      <td class="px-4 py-2 text-right text-gray-700">{{ branch.number_of_tickets }}</td>
                      <td class="px-4 py-2 text-right text-gray-700">{{ integerToCurrency(branch.average_per_ticket) }}</td>
                      <td class="px-4 py-2 text-right text-success-500 font-medium">{{ branch.new_clients }}</td>
                      <td class="px-4 py-2 text-right text-gray-700">{{ parseFloat(branch.new_clients_average).toFixed(1) }}%</td>
                      <td class="px-4 py-2 text-right text-error-500 font-medium">{{ branch.canceled_quotes }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import { formatDatetime } from "@/utils/dateFormatter";
import { integerToCurrency } from "@/utils/currencyFormatter";
export default {
  name: "Monthly Sales",

  data() {
    return {
      report: null,
      loading: false,
      selectedYear: new Date().getFullYear(),
      selectedMonth: new Date().getMonth() + 1,
      expandedFranchises: [],
      months: [
        { number: 1, label: "Enero" },
        { number: 2, label: "Febrero" },
        { number: 3, label: "Marzo" },
        { number: 4, label: "Abril" },
        { number: 5, label: "Mayo" },
        { number: 6, label: "Junio" },
        { number: 7, label: "Julio" },
        { number: 8, label: "Agosto" },
        { number: 9, label: "Septiembre" },
        { number: 10, label: "Octubre" },
        { number: 11, label: "Noviembre" },
        { number: 12, label: "Diciembre" },
      ],
    };
  },

  computed: {
    availableYears() {
      const currentYear = new Date().getFullYear();
      const startingYear = 2020;
      const years = [];

      for (let year = startingYear; year <= currentYear; year++) {
        years.push(year);
      }

      return years;
    },

    availableMonths() {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;

      if (this.selectedYear === currentYear) {
        return this.months.filter((month) => month.number <= currentMonth);
      }

      return this.months;
    },
  },

  created() {
    this.loadReport();
  },

  methods: {
    formatDatetime,

    integerToCurrency,

    toggleFranchise(id) {
      const index = this.expandedFranchises.indexOf(id);
      if (index === -1) {
        this.expandedFranchises.push(id);
      } else {
        this.expandedFranchises.splice(index, 1);
      }
    },

    isFranchiseExpanded(id) {
      return this.expandedFranchises.includes(id);
    },

    async loadReport() {
      this.loading = true;

      try {
        this.report = await api.reports.getMonthlyReport([
          { name: "year", value: this.selectedYear },
          {
            name: "month",
            value: this.selectedMonth,
          },
        ]);
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    selectYear(year) {
      if (year === this.selectedYear) {
        return;
      }

      this.selectedYear = year;
      this.selectedMonth = null;
      this.report = null;
    },

    selectMonth(month) {
      if (this.selectedMonth === month) {
        return;
      }

      this.selectedMonth = month.number;

      this.loadReport();
    },
  },
};
</script>
